import type { VideoTemplate, TemplateLayout, TemplateTheme } from '@/types/template';
import { TEMPLATE_LAYOUTS } from './layouts';
import { TEMPLATE_THEMES } from './themes';

const OUTLINED_CAPTION_PRESETS = [
    { fontFamily: 'Jua', fontWeight: 'normal' as const, fontSize: 70, captionY: 0.82 },
    { fontFamily: 'Do Hyeon', fontWeight: 'normal' as const, fontSize: 74, captionY: 0.74 },
    { fontFamily: 'Nanum Myeongjo', fontWeight: 'bold' as const, fontSize: 66, captionY: 0.2 },
    { fontFamily: 'Gothic A1', fontWeight: 'bold' as const, fontSize: 68, captionY: 0.52 },
    { fontFamily: 'Noto Sans KR', fontWeight: 'black' as const, fontSize: 72, captionY: 0.82 },
    { fontFamily: 'Nanum Pen Script', fontWeight: 'normal' as const, fontSize: 78, captionY: 0.18 },
];

const BACKGROUND_CAPTION_THEME_IDS = new Set([
    'issue_breaking_red',
    'issue_luxury_gold',
]);

/**
 * 레이아웃과 테마를 조합하여 최종 VideoTemplate 생성
 */
export function mergeLayoutAndTheme(
    layout: TemplateLayout,
    theme: TemplateTheme
): Omit<VideoTemplate, 'id' | 'name' | 'layoutId' | 'themeId' | 'version'> {
    const presetSeed = `${layout.id}:${theme.id}`
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const captionPreset = OUTLINED_CAPTION_PRESETS[presetSeed % OUTLINED_CAPTION_PRESETS.length];
    const keepCaptionBackground = BACKGROUND_CAPTION_THEME_IDS.has(theme.id);
    const result: any = {
        description: `${layout.name} 레이아웃 - ${theme.name} 테마`,
        layout: {
            x: layout.content.x,
            y: layout.content.y,
            width: layout.content.width,
            height: layout.content.height,
            scale: layout.content.scale,
            objectFit: layout.content.objectFit || 'cover'
        }
    };

    // Caption 스타일 적용
    if (layout.caption) {
        result.textStyle = {
            fontFamily: captionPreset.fontFamily,
            fontSize: captionPreset.fontSize,
            fontWeight: captionPreset.fontWeight,
            color: '#ffffff',
            backgroundColor: keepCaptionBackground ? theme.colors.textBg : 'rgba(0, 0, 0, 0)',
            textAlign: 'center' as const,
            verticalAlign: 'middle' as const,
            captionY: layout.id === 'basic' ? captionPreset.captionY : layout.caption.y,
            width: layout.caption.width || 1080,
            height: layout.caption.height || 180,
            x: layout.caption.x ?? 0,
            letterSpacing: captionPreset.fontFamily === 'Nanum Myeongjo' ? -1 : 0,
            stroke: {
                width: 6,
                color: '#000000'
            },
            textShadow: keepCaptionBackground
                ? undefined
                : {
                    offsetX: 0,
                    offsetY: 5,
                    blur: 10,
                    color: 'rgba(0, 0, 0, 0.45)'
                }
        };
    }

    // Background 적용
    if (theme.backgroundImage) {
        result.background = {
            type: 'image' as const,
            value: theme.backgroundImage,
            opacity: 1
        };
    } else {
        result.background = {
            type: 'color' as const,
            value: theme.colors.background,
            opacity: 1
        };
    }

    // Title 적용 (issue, ssul 레이아웃용)
    if (layout.title) {
        // 타이틀 위치 및 간격 최적화 (겹침 방지 및 텍스트 짤림 방지)
        // 화면 최상단 UI(뒤로가기 등)와 겹치지 않도록 Y축 하향 조정
        const titlePosition = {
            x: layout.title.x ?? 0,
            y: layout.title.y ?? -750
        };

        const secondaryPosition = {
            x: layout.title.secondaryX ?? 0,
            y: layout.title.secondaryY ?? -570
        };

        const isSsul = layout.id === 'ssul';

        result.title = {
            text: layout.id.startsWith('issue')
                ? "국군이 기쁨조?\n박충권 망언어 초토화"
                : "채널명\n오늘의 썰 제목",
            style: {
                fontFamily: theme.fonts.title.family,
                fontSize: isSsul ? 80 : theme.fonts.title.size,
                fontWeight: theme.fonts.title.weight as any,
                color: theme.colors.title1 || (layout.id.startsWith('issue') ? theme.colors.text : theme.colors.primary),
                backgroundColor: theme.colors.title1Bg,
                textAlign: 'center' as const,
                verticalAlign: 'middle' as const,
                width: layout.title.width || 1080,
                stroke: undefined,
                letterSpacing: 0
            },
            height: layout.title.height || 180, // Default to 180 to prevent clipping
            secondaryHeight: layout.title.secondaryHeight || 180, // Default to 180
            secondaryStyle: {
                fontFamily: theme.fonts.title2?.family || theme.fonts.title.family,
                fontSize: isSsul ? 80 : (theme.fonts.title2?.size || theme.fonts.title.size),
                fontWeight: (theme.fonts.title2?.weight || theme.fonts.title.weight) as any,
                color: theme.colors.title2 || (layout.id.startsWith('issue')
                    ? (theme.colors.accent || theme.colors.primary)
                    : theme.colors.primary),
                backgroundColor: theme.colors.title2Bg,
                textAlign: 'center' as const,
                verticalAlign: 'middle' as const,
                width: layout.title.width || 1080,
                stroke: undefined,
                letterSpacing: 0
            },
            position: titlePosition,
            secondaryPosition: secondaryPosition,
            isDoubleLine: layout.title.isDoubleLine
        };
    }

    return result;
}

/**
 * 모든 레이아웃 × 테마 조합으로 템플릿 자동 생성
 */
export function generateAllTemplates(): VideoTemplate[] {
    const templates: VideoTemplate[] = [];

    for (const [layoutId, layout] of Object.entries(TEMPLATE_LAYOUTS)) {
        for (const [themeId, theme] of Object.entries(TEMPLATE_THEMES)) {
            const isSsulLayout = layoutId === 'ssul';
            const isSsulTheme = themeId.startsWith('ssul_');

            // 썰 레이아웃은 썰 테마와만 조합
            if (isSsulLayout && !isSsulTheme) {
                continue;
            }

            // 썰 테마는 썰 레이아웃과만 조합
            if (!isSsulLayout && isSsulTheme) {
                continue;
            }

            const merged = mergeLayoutAndTheme(layout, theme);

            templates.push({
                id: `${layoutId}-${themeId}`,
                name: `${layout.name} - ${theme.name}`,
                layoutId,
                themeId,
                version: '1.0.0',
                ...merged
            });
        }
    }

    return templates;
}

/**
 * 특정 레이아웃에 적용 가능한 테마 목록 가져오기
 */
export function getCompatibleThemes(layoutId: string): TemplateTheme[] {
    if (layoutId === 'ssul') {
        return Object.values(TEMPLATE_THEMES).filter(theme => theme.id.startsWith('ssul_'));
    }
    return Object.values(TEMPLATE_THEMES).filter(theme => !theme.id.startsWith('ssul_'));
}

/**
 * 특정 테마에 적용 가능한 레이아웃 목록 가져오기
 */
export function getCompatibleLayouts(themeId: string): TemplateLayout[] {
    if (themeId.startsWith('ssul_')) {
        const ssulLayout = TEMPLATE_LAYOUTS['ssul'];
        return ssulLayout ? [ssulLayout] : [];
    }
    return Object.values(TEMPLATE_LAYOUTS).filter(layout => layout.id !== 'ssul');
}
